import React, { useCallback, useMemo } from 'react';
import Conditional from '../../../common/conditional';

import _ from 'lodash';

import classNames from './style.module.scss';
import russelsmithLogo from '../../../../assets/images/brand.png';

import { concatClassNames, formatTime, getExtractedInspectionName, getFacilityLogo, getInitials, getInternalReviewDetails, getPrefixedModule } from '../../../../utils/utilities';
import { Project, User } from '../../../../utils/models';
import { getCurrentUser } from '../../../../services/authService';
import { APP_DATE_FORMAT } from '../../../../utils/constants';
import { SHELL_ID } from '../../../../utils/ids';

interface Props {
	reportTitle: string;
	workOrderNumber: string;
	projectDetails: Project
}

const CustomInfoSheet: React.FC<Props> = ({ projectDetails, reportTitle, workOrderNumber }) => {
	const { createdAt, inspectionTypeName, module, organisation, documentNumber, equipmentTag, facility, report, reviewer, revisionNumber, supervisorId } = projectDetails;
	const { id, firstName, lastName } = getCurrentUser() as User;

	const inspectorName = id === supervisorId ?  `${firstName} ${lastName}` : report.creatorName;
	const inspectionTypeLabel = getExtractedInspectionName(inspectionTypeName);

	const formatOrganizationName = useCallback(() => {
		if (organisation._id === SHELL_ID) {
			return 'SNEPCO';
		}

		return organisation.organisationName.toUpperCase();
	}, [organisation]);

	const reviews = useMemo(() => {
		let counter = revisionNumber ?? 0;

		let reviews = _.orderBy(reviewer, ['createdAt'], ['desc'])
						.filter((_, index) => index < counter)
						.slice(0, 3)
						.map((review, index) => ({ id: counter--, ...review }))

		return reviews;
	}, [reviewer, revisionNumber]);

	const internalReviewer = useMemo(() => {
		return getInternalReviewDetails(projectDetails)
	}, [projectDetails]);

	return (
		<div className="p-3 pdf-page" aria-label="Information Sheet">
			<div className="border-dark d-flex  flex-column justify-content-center align-items-center">
				<div className={classNames['custom-info-sheet']}>
					<div className={classNames['custom-info-sheet-center']}>
						<img 
							src={organisation.organisationLogo} 
							alt={organisation.organisationName} 
							className={classNames['custom-info-sheet-organization']} 
						/>

						<Conditional isVisible={!!getFacilityLogo(organisation._id, facility.facilityLogo)}>
							<img
								src={facility.facilityLogo}
								alt={facility.facilityName}
								className={classNames['custom-info-sheet-facility']}
							/>
						</Conditional>

						<h1>{facility.facilityName}</h1>

						<img src={russelsmithLogo} alt="Russel Smith" />

						<Conditional isVisible={false}>
							<h2>{reportTitle} </h2>
						</Conditional>

						<h2>{inspectionTypeLabel} </h2>

						{module ? <h3>{getPrefixedModule(module)}</h3> : null}
						
						<h3>{equipmentTag} </h3>

						<div className={classNames['custom-info-sheet-document']}>
							<Conditional isVisible={workOrderNumber.length > 0}>
								<div className="container-fluid">
									<div className={concatClassNames([classNames['work-order-number'],'row'])}>
										<div className={concatClassNames([classNames['work-order-number-item'],classNames['work-order-number-item-bordered'],'col-4'])}>
											Work Order Number:
										</div>
										<div className={concatClassNames([classNames['work-order-number-item'], 'col'])}>
											{workOrderNumber}
										</div>
									</div>
								</div>
							</Conditional>

							<h4>REPORT NO: {documentNumber}</h4>
						</div>
					</div>

					<div className="container-fluid mt-auto overflow-hidden">
						<Conditional isVisible={reviews.length > 0}>
							{reviews.map((review) => (
								<div className="row" key={review._id}>
									<div className={classNames['pdf-overview'] + ' col'}>R{review.id.toString().padStart(2, '0')}</div>
									<div className={classNames['pdf-overview'] + ' col'}>
										{formatTime(review.createdAt, APP_DATE_FORMAT)}
									</div>
									<div className={classNames['pdf-overview'] + ' col'}>
										Issued for Review and approval
									</div>
									<div className={classNames['pdf-overview'] + ' col'}>
										{inspectorName ? getInitials(inspectorName) : 'N/A'}
									</div>
									<div className={classNames['pdf-overview'] + ' col'}>
										{getInitials(review.reviewerName)}
									</div>
									<div className={classNames['pdf-overview'] + ' col'}>
										{formatOrganizationName()}
									</div>
								</div>
							))}
						</Conditional>
						
						<Conditional isVisible={reviews.length === 0}>
							<div className="row">
								<div className={classNames['pdf-overview'] + ' col'}>R01</div>
								<div className={classNames['pdf-overview'] + ' col'}>
									{formatTime(new Date(), APP_DATE_FORMAT)}
								</div>
								<div className={classNames['pdf-overview'] + ' col'}>
									Issued for Review and approval
								</div>
								<div className={classNames['pdf-overview'] + ' col'}>
									{inspectorName ? getInitials(inspectorName) : 'N/A'}
								</div>
								<div className={classNames['pdf-overview'] + ' col'}>
									{projectDetails.projectActivator ? getInitials(projectDetails.projectActivator) : 'N/A'}
								</div>
								<div className={classNames['pdf-overview'] + ' col'}>
									{formatOrganizationName()}
								</div>
							</div>
						</Conditional>

						<div className="row">
							<div className={classNames['pdf-overview-title'] + ' col'}>
								Rev
							</div>
							<div className={classNames['pdf-overview-title'] + ' col'}>
								Date
							</div>
							<div className={classNames['pdf-overview-title'] + ' col'}>
								Description
							</div>
							<div className={classNames['pdf-overview-title'] + ' col'}>
								Orig
							</div>
							<div className={classNames['pdf-overview-title'] + ' col'}>
								Chk
							</div>
							<div className={classNames['pdf-overview-title'] + ' col'}>
								Appr
							</div>
						</div>

						<div className="row">
							<div className={classNames['pdf-approval'] + ' col'}>
								<div className="row mb-4">
									<div className="col">APPROVED BY:</div>
									<div className="col text-uppercase text-right">{internalReviewer.reviewerName}</div>
								</div>
								<div className="row">
									<div className="col">DATE:</div>
									<div className="col text-uppercase text-right">{internalReviewer.reviewDate}</div>
								</div>
							</div>
							<div className={classNames['pdf-approval'] + ' col'}>
								<div className="row mb-4">
									<div className="col">INSPECTION START DATE:</div>
									<div className="col text-uppercase text-right">{formatTime(createdAt, APP_DATE_FORMAT)}</div>
								</div>
								<div className="row">
									<div className="col">INSPECTION COMPLETION DATE:</div>
									<div className="col text-uppercase text-right">{formatTime(report.createdAt || new Date(), APP_DATE_FORMAT)}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CustomInfoSheet;
